import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Wallet, IndianRupee } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import { cities, citiesByRegion, type CityInfo } from '@shared/cities';

const REGION_ORDER: CityInfo['region'][] = [
  'North India',
  'West India',
  'South India',
  'East India',
  'Central India',
  'Northeast India',
];

export default function CityIndexPage() {
  const { isDark } = useTheme();

  const grouped = useMemo(() => citiesByRegion(), []);
  const stateCapitalCount = useMemo(
    () => cities.filter((c) => c.isStateCapital).length,
    []
  );

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Cities', url: '/city' },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={pageSeoMeta.cityIndex.title}
        description={pageSeoMeta.cityIndex.description}
        canonical={pageSeoMeta.cityIndex.canonical}
        jsonLd={buildBreadcrumbJsonLd(breadcrumbItems)}
      />

      <Breadcrumb
        items={[
          { name: 'Home', href: '/' },
          { name: 'Cities' },
        ]}
      />

      {/* Header */}
      <div className={`border-b transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 text-sm mb-2">
            <MapPin className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
              {cities.length} cities · {stateCapitalCount} state capitals
            </span>
          </div>
          <h1 className={`text-3xl sm:text-4xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Nicotine Pouch Delivery Across India
          </h1>
          <p className={`mt-3 max-w-3xl leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            We ship nicotine pouches to every pincode in India. Below are featured delivery
            guides for {cities.length} cities — including all 28 state capitals — covering
            the buyer profile, popular brands, and any region-specific delivery notes for
            each. If your city isn't listed, you can still order from us under the same
            standard pan-India shipping timeline.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* Uniform delivery summary card */}
        <section
          className={`rounded-2xl border p-6 sm:p-8 ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <h2 className={`text-xl font-bold mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            How delivery works across India
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <DeliveryStat
              icon={<Truck className="w-5 h-5" />}
              title="Standard delivery"
              value="2–4 business days · pan-India"
              isDark={isDark}
            />
            <DeliveryStat
              icon={<IndianRupee className="w-5 h-5" />}
              title="Free shipping"
              value="On orders over ₹5,000"
              isDark={isDark}
            />
            <DeliveryStat
              icon={<Wallet className="w-5 h-5" />}
              title="Payment"
              value="UPI, cards, COD on most orders"
              isDark={isDark}
            />
          </div>
        </section>

        {/* Region sections */}
        {REGION_ORDER.map((region) => {
          const list = grouped[region] ?? [];
          if (list.length === 0) return null;
          return (
            <section key={region}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {region}
                </h2>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {list.length} {list.length === 1 ? 'city' : 'cities'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/city/${c.slug}`}
                    className={`block rounded-xl border p-4 transition-colors group ${
                      isDark
                        ? 'bg-gray-900 border-gray-800 hover:border-emerald-500/50'
                        : 'bg-white border-gray-200 hover:border-emerald-500/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {c.name}
                        </h3>
                        <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {c.state}
                        </p>
                      </div>
                      {c.isStateCapital && (
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap ${
                            isDark
                              ? 'bg-emerald-900/40 text-emerald-300'
                              : 'bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          State Capital
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-xs mt-3 font-medium ${
                        isDark ? 'text-emerald-400 group-hover:text-emerald-300' : 'text-emerald-600 group-hover:text-emerald-700'
                      }`}
                    >
                      View delivery guide →
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {/* Reassurance footer */}
        <section
          className={`rounded-2xl border p-6 sm:p-8 ${
            isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
          }`}
        >
          <h2 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Don't see your city listed?
          </h2>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            These are featured delivery guides — we ship to every pincode across India
            under the same standard timeline. If you are buying from a city not listed
            above, your order will still ship from our warehouse on the standard 2–4
            business day timeline, with Cash on Delivery available on most pincodes.
            Place your order on the <Link to="/store" className="text-emerald-500 hover:text-emerald-400 underline">store</Link>{' '}
            and choose your address at checkout.
          </p>
        </section>
      </div>
    </div>
  );
}

function DeliveryStat({
  icon,
  title,
  value,
  isDark,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  isDark: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-4 border ${
        isDark ? 'bg-gray-950 border-gray-800' : 'bg-gray-50 border-gray-100'
      }`}
    >
      <div className={`flex items-center gap-2 mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {icon}
        <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {title}
        </span>
      </div>
      <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
